import "@/App.css";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import ChatPage from "@/pages/ChatPage";
import JournalPage from "@/pages/JournalPage";
import MeditationsPage from "@/pages/MeditationsPage";
import MilestonesPage from "@/pages/MilestonesPage";
import EchoMapPage from "@/pages/EchoMapPage";
import ProfilePage from "@/pages/ProfilePage";
import BreathworkPage from "@/pages/BreathworkPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import { Sidebar } from "@/components/Sidebar";
import SovereignAnchor from "@/components/SovereignAnchor";
import BetaFeedbackWidget from "@/components/BetaFeedbackWidget";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import EmailVerificationGate from "@/components/EmailVerificationGate";
import AdminDashboardPage from "@/pages/AdminDashboardPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand relative overflow-hidden" data-testid="loading-screen">
        <div className="ethereal-glow-gold animate-drift" style={{ top: '-100px', right: '-50px' }} />
        <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-100px', left: '-50px' }} />
        <div className="text-center relative z-10">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
            <div className="absolute inset-3 rounded-full bg-gold/10 animate-breathe" style={{ animationDelay: '0.5s' }} />
            <div className="absolute inset-6 rounded-full bg-gold/20 animate-breathe" style={{ animationDelay: '1s' }} />
          </div>
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase">Centering...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gate app behind onboarding
  if (!user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Starfield = () => {
  const stars = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 2 + 1,
    duration: Math.random() * 4 + 3,
    delay: Math.random() * 5,
    opacity: Math.random() * 0.4 + 0.1,
  }));

  return (
    <div className="starfield">
      {stars.map(s => (
        <div
          key={s.id}
          className="star"
          style={{
            left: s.left,
            top: s.top,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
            opacity: s.opacity,
          }}
        />
      ))}
    </div>
  );
};

const SacredGeometry = () => (
  <div className="sacred-geometry" style={{ top: '10%', right: '5%', width: '500px', height: '500px' }}>
    <svg viewBox="0 0 200 200" width="100%" height="100%">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
      <circle cx="100" cy="100" r="60" fill="none" stroke="#C6B28F" strokeWidth="0.2" />
      <circle cx="100" cy="100" r="40" fill="none" stroke="#C6B28F" strokeWidth="0.3" />
      <circle cx="100" cy="40" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
      <circle cx="100" cy="160" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
      <circle cx="40" cy="100" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
      <circle cx="160" cy="100" r="40" fill="none" stroke="#7A8274" strokeWidth="0.2" />
      <polygon points="100,20 180,140 20,140" fill="none" stroke="#C6B28F" strokeWidth="0.2" />
      <polygon points="100,180 20,60 180,60" fill="none" stroke="#C6B28F" strokeWidth="0.2" />
    </svg>
  </div>
);

const DashboardLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  // Hard email-verification gate: only Profile is reachable until verified.
  const verifyExempt = location.pathname.startsWith('/profile');
  const needsVerify = user && !user.email_verified && !verifyExempt;
  return (
    <div className="min-h-screen bg-sand relative overflow-hidden">
      <div className="noise-overlay" />
      <Starfield />
      <SacredGeometry />
      {/* Ethereal background glows */}
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px', zIndex: 0 }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-120px', left: '-80px', zIndex: 0 }} />
      <div className="ethereal-glow-warm" style={{ top: '30%', left: '50%', transform: 'translateX(-50%)', zIndex: 0 }} />
      {/* Floating orbs */}
      <div className="floating-orb floating-orb-gold" style={{ width: 80, height: 80, top: '15%', right: '20%', animationDuration: '8s', zIndex: 0 }} />
      <div className="floating-orb floating-orb-moss" style={{ width: 60, height: 60, bottom: '25%', left: '15%', animationDuration: '10s', animationDelay: '2s', zIndex: 0 }} />
      <div className="floating-orb floating-orb-gold" style={{ width: 40, height: 40, top: '60%', right: '10%', animationDuration: '7s', animationDelay: '4s', zIndex: 0 }} />
      <Sidebar />
      <main className="lg:pl-64 min-h-screen relative z-10">
        <div className="p-6 lg:p-10 max-w-[1400px] mx-auto pb-24 lg:pb-10">
          {needsVerify ? <EmailVerificationGate /> : <Outlet />}
        </div>
      </main>
      <div className="lg:hidden h-20" />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously for OAuth session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:conversationId" element={<ChatPage />} />
        <Route path="journal" element={<JournalPage />} />
        <Route path="meditations" element={<MeditationsPage />} />
        <Route path="breathwork" element={<BreathworkPage />} />
        <Route path="echo-map" element={<EchoMapPage />} />
        <Route path="milestones" element={<MilestonesPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="admin" element={<AdminDashboardPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-sand p-6" data-testid="error-boundary-fallback">
          <div className="max-w-md w-full text-center">
            <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Something cracked open</p>
            <h1 className="font-serif text-3xl text-dark mb-3 mystical-heading">A moment, please</h1>
            <p className="text-text-muted font-sans text-sm leading-relaxed mb-6">
              An unexpected error occurred. We've been notified and are looking into it.
              Take three breaths, then try again.
            </p>
            <details className="text-left mb-6 text-xs text-text-muted/70 font-mono bg-bone/40 p-3 rounded-lg border border-black/5">
              <summary className="cursor-pointer">Details</summary>
              <pre className="mt-2 whitespace-pre-wrap">{error?.message || 'Unknown error'}</pre>
            </details>
            <button
              onClick={resetError}
              data-testid="error-boundary-retry"
              className="h-11 px-6 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    >
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <SovereignAnchorMount />
          <BetaFeedbackMount />
          <InstallPWAPrompt />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

// Render Anchor only when user is authenticated
function SovereignAnchorMount() {
  const { user } = useAuth();
  if (!user) return null;
  return <SovereignAnchor />;
}

function BetaFeedbackMount() {
  const { user } = useAuth();
  if (!user) return null;
  return <BetaFeedbackWidget />;
}

export default App;
